import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatToYYYY_MMM_DD } from '../../../../../helpers/functions';
import { fetchLifestyleHistory } from '../../../../../redux/modules/patient/actions';
import {
  alcoholEnum,
  smokingEnum,
  exerciseEnum,
  recreationalDrugsEnum,
  caffeineEnum,
  exerciseTypeEnum,
} from '../../../clinical-services/service-steps/clinical-assessment/data';
import CustomInput from '../../../../../components/common/CustomInput';
import SortIcon from '../../../../../components/common/SortIcon';
import { useTruePagination } from '../../../../../helpers/true-pagination';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Service Type options based on backend documentation
const serviceTypeOptions = [
  { value: 'All', label: 'All' },
  { value: 'Allergic Rhinitis', label: 'Allergic Rhinitis' },
  { value: 'Patient Chart', label: 'Patient Chart' },
  { value: 'Minor Ailments', label: 'Minor Ailments' },
  { value: 'Annual Medication Review', label: 'Annual Medication Review' },
  { value: 'Urinary Tract Infection', label: 'Urinary Tract Infection' },
];

const LifestyleHistoryTab = ({ patientId }) => {
  const dispatch = useDispatch();
  const { lifestyleHistory, lifestyleHistoryPagination, loading } = useSelector((state) => state.patients);
  // Watch clinical service updates to refresh lifestyle history
  const { clinicalServiceObj } = useSelector((state) => state.clinicalService);

  // Filter and search state
  const [search, setSearch] = useState('');
  const [serviceType, setServiceType] = useState('All');
  const [dateRecorded, setDateRecorded] = useState('');
  const [sort, setSort] = useState('dateRecorded');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const lastClinicalServiceUpdateRef = useRef(null);

  // Debounce search input
  const [searchDebounce, setSearchDebounce] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data when filters change
  useEffect(() => {
    const queryParams = {
      page,
      size,
      sort,
      order,
    };

    if (searchDebounce) {
      queryParams.search = searchDebounce;
    }

    if (serviceType && serviceType !== 'All') {
      queryParams.serviceType = serviceType;
    }

    if (dateRecorded) {
      queryParams.dateRecorded = dateRecorded;
    }

    dispatch(fetchLifestyleHistory(patientId, queryParams));
  }, [patientId, dispatch, page, size, sort, order, searchDebounce, serviceType, dateRecorded]);

  // Watch for clinical service updates and refresh lifestyle history
  useEffect(() => {
    if (clinicalServiceObj?.initialAssessment?.initialAssessmentForm) {
      const clinicalAssessment = clinicalServiceObj.initialAssessment.initialAssessmentForm.clinicalAssessment;
      const healthAndWellness = clinicalServiceObj.initialAssessment.initialAssessmentForm.healthAndWellness;
      const lifestyleData = clinicalAssessment || healthAndWellness;
      
      if (lifestyleData) {
        // Create a unique key from lifestyle data to detect changes
        const lifestyleKey = JSON.stringify({
          alcohol: lifestyleData.alcohol,
          caffeine: lifestyleData.caffeine,
          smoking: lifestyleData.smoking,
          exercise: lifestyleData.exercise,
        });
        
        // Only refresh if the data has actually changed
        if (lifestyleKey !== lastClinicalServiceUpdateRef.current) {
          lastClinicalServiceUpdateRef.current = lifestyleKey;
          
          // Refresh lifestyle history after a short delay to allow backend processing
          const refreshTimer = setTimeout(() => {
            const queryParams = {
              page: 1, // Reset to first page
              size,
              sort,
              order,
            };

            if (searchDebounce) {
              queryParams.search = searchDebounce;
            }

            if (serviceType && serviceType !== 'All') {
              queryParams.serviceType = serviceType;
            }

            if (dateRecorded) {
              queryParams.dateRecorded = dateRecorded;
            }

            dispatch(fetchLifestyleHistory(patientId, queryParams));
            setPage(1); // Reset to first page
          }, 1000); // 1 second delay to allow backend to process
          
          return () => clearTimeout(refreshTimer);
        }
      }
    }
  }, [clinicalServiceObj, patientId, dispatch, size, sort, order, searchDebounce, serviceType, dateRecorded]);

  const handleSort = (newSort) => {
    if (sort === newSort) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(newSort);
      setOrder('desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleClearFilters = () => {
    setSearch('');
    setServiceType('All');
    setDateRecorded('');
    setSort('dateRecorded');
    setOrder('desc');
    setPage(1);
  };

  const paginationRange = useTruePagination({
    totalPageCount: lifestyleHistoryPagination?.totalPages || 1,
    siblingCount: 1,
    currentPage: page,
  });

  const hasFilters = search || (serviceType && serviceType !== 'All') || dateRecorded;

  // Control table scrolling: limit height when more than 6 rows
  const rowsCount = lifestyleHistory ? lifestyleHistory.length : 0;
  const shouldLimitHeight = rowsCount > 6;
  return (
    <div className="w-full h-full overflow-y-auto bg-white p-6">
      <div className="max-w-full mx-auto">
        {/* Filters Section */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-end gap-6 justify-between">
            {/* Search Input */}
            <div className="relative w-[500px]">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="block w-full h-[72px] pl-10 pr-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-teal-200 focus:border-teal-200 text-[18px]"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            {/* Right Side Filters */}
            <div className="flex flex-wrap items-end gap-6">
              {/* Service Type Dropdown */}
              <div className="flex-shrink-0 w-[300px]">
                <CustomInput
                  options={serviceTypeOptions}
                  name="serviceType"
                  label="Service Type"
                  value={serviceType}
                  type="dropdown"
                  showOptionalLabel={false}
                  onChange={(e) => {
                    setServiceType(e.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* Date Recorded */}
              <div className="flex-shrink-0 w-[300px]">
                <CustomInput
                  name="dateRecorded"
                  label="Date Recorded"
                  type="date"
                  value={dateRecorded}
                  onChange={(value) => {
                    setDateRecorded(value || '');
                    setPage(1);
                  }}
                  showOptionalLabel={false}
                  clearableDatePicker={!!dateRecorded}
                  onClear={() => {
                    setDateRecorded('');
                    setPage(1);
                  }}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Table Section */}
        {loading && (!lifestyleHistory || lifestyleHistory.length === 0) ? (
          <div className="w-full h-full flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 border-t-transparent border-r-transparent mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading lifestyle history...</p>
            </div>
          </div>
        ) : !lifestyleHistory || lifestyleHistory.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-lg text-gray-600">No lifestyle history data available</p>
              <p className="text-sm text-gray-500 mt-2">Lifestyle records will appear here once they are created.</p>
            </div>
          </div>
        ) : (
          <>
            <div className={`overflow-x-auto border border-gray-200 rounded-lg ${shouldLimitHeight ? 'max-h-[392px] overflow-y-auto' : ''}`}>
              <table className="min-w-full min-w-[1200px] divide-y divide-gray-200">
                <thead className="bg-gray-20">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('alcohol')}
                    >
                      <div className="flex items-center">
                        Alcohol
                        <span className="ml-2">
                          <SortIcon
                            isDownActive={sort === 'alcohol' && order === 'desc'}
                            isUpActive={sort === 'alcohol' && order === 'asc'}
                            className="text-gray-600"
                          />
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('caffeine')}
                    >
                      <div className="flex items-center">
                        Caffeine
                        <span className="ml-2">
                          <SortIcon
                            isDownActive={sort === 'caffeine' && order === 'desc'}
                            isUpActive={sort === 'caffeine' && order === 'asc'}
                            className="text-gray-600"
                          />
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('smoking')}
                    >
                      <div className="flex items-center">
                        Smoking
                        <span className="ml-2">
                          <SortIcon
                            isDownActive={sort === 'smoking' && order === 'desc'}
                            isUpActive={sort === 'smoking' && order === 'asc'}
                            className="text-gray-600"
                          />
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider"
                    >
                      Recreational Drugs
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider"
                    >
                      Exercise
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider"
                    >
                      Exercise Types
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider"
                    >
                      Comments
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('serviceType')}
                    >
                      <div className="flex items-center">
                        Service Type
                        <span className="ml-2">
                          <SortIcon
                            isDownActive={sort === 'serviceType' && order === 'desc'}
                            isUpActive={sort === 'serviceType' && order === 'asc'}
                            className="text-gray-600"
                          />
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('dateRecorded')}
                    >
                      <div className="flex items-center">
                        Date Recorded
                        <span className="ml-2">
                          <SortIcon
                            isDownActive={sort === 'dateRecorded' && order === 'desc'}
                            isUpActive={sort === 'dateRecorded' && order === 'asc'}
                            className="text-gray-600"
                          />
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lifestyleHistory.map((record, i) => {
                    const isStripedRow = i % 2 === 1;
                    return (
                    <tr key={record.historyId || record.id} className={`hover:bg-gray-50 ${isStripedRow ? 'bg-gray-10' : 'bg-white'}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const option = alcoholEnum.options.find(x => x.value === record.alcohol);
                          return option?.header || record.alcohol || 'Not Assessed';
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const option = caffeineEnum.options.find(x => x.value === record.caffeine);
                          return option?.header || record.caffeine || 'Not Assessed';
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const option = smokingEnum.options.find(x => x.value === record.smoking);
                          return option?.header || record.smoking || 'Not Assessed';
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(() => {
                          if (!record.recreationalDrugs) {
                            return 'None';
                          }
                          // Handle both string (comma-separated) and array formats
                          const drugs = typeof record.recreationalDrugs === 'string' 
                            ? record.recreationalDrugs.split(',').map(d => d.trim())
                            : Array.isArray(record.recreationalDrugs) 
                              ? record.recreationalDrugs 
                              : [];
                          
                          if (drugs.length === 0) return 'None';
                          
                          return drugs
                            .map(drug => {
                              // Check if this is "Other" and replace with custom text
                              if (drug?.toLowerCase() === "other" || drug === "Other") {
                                return record.recreationalDrugsOther || "Other";
                              }
                              const option = recreationalDrugsEnum.options.find(x => x.value === drug);
                              return option?.header || drug;
                            })
                            .join(', ');
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const option = exerciseEnum.options.find(x => x.value === record.exercise);
                          return option?.header || record.exercise || 'Not Assessed';
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(() => {
                          if (!record.exerciseTypes || record.exercise === 'Never' || record.exercise === 'Sports') {
                            return '-';
                          }
                          // Handle both array and string formats
                          const exerciseTypes = Array.isArray(record.exerciseTypes)
                            ? record.exerciseTypes
                            : typeof record.exerciseTypes === 'string'
                              ? record.exerciseTypes.split(',').map(t => t.trim()).filter(t => t)
                              : [];
                          
                          if (exerciseTypes.length === 0) return '-';
                          
                          return exerciseTypes
                            .map(type => {
                              const typeOption = exerciseTypeEnum.options.find(x => x.value === type);
                              return typeOption?.header || type;
                            })
                            .join(', ');
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={record.comments || ''}>
                        {record.comments || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.serviceType || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          // Backend uses LastLifeStyleDate as primary source, with fallback to CreatedDate
                          // For history records, dateRecorded should be available, but prioritize lastLifeStyleDate
                          const dateValue = record.lastLifeStyleDate || record.dateRecorded || record.createdDate;
                          
                          if (!dateValue || (typeof dateValue === 'string' && dateValue.includes('0001-01-01'))) {
                            return '-';
                          }
                          
                          return formatToYYYY_MMM_DD(dateValue);
                        })()}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {lifestyleHistoryPagination && lifestyleHistoryPagination.totalPages > 1 && (
              <div className="flex items-center justify-center px-4 py-4 border-t border-gray-200 bg-gray-50 sm:px-6 mt-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handlePageChange(page === 1 ? 1 : page - 1)}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-4 py-2 bg-white font-semibold shadow rounded-md ${
                      page === 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  {paginationRange?.map((item, index) => (
                    <React.Fragment key={index}>
                      {item === '...' ? (
                        <span className="relative inline-flex items-center bg-white px-4 py-2 text-gray-800 font-semibold shadow rounded-md">
                          ...
                        </span>
                      ) : (
                        <button
                          onClick={() => handlePageChange(item)}
                          className={`relative inline-flex items-center ${
                            page === item
                              ? 'bg-teal-600 text-white'
                              : 'bg-white text-gray-800 hover:bg-gray-50'
                          } px-4 py-2 font-semibold shadow rounded-md`}
                        >
                          {item}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                  <button
                    onClick={() =>
                      handlePageChange(
                        lifestyleHistoryPagination.totalPages === page || lifestyleHistoryPagination.totalPages === 0
                          ? page
                          : page + 1
                      )
                    }
                    disabled={lifestyleHistoryPagination.totalPages === page || lifestyleHistoryPagination.totalPages === 0}
                    className={`relative inline-flex items-center px-4 py-2 bg-white font-semibold shadow rounded-md ${
                      lifestyleHistoryPagination.totalPages === page || lifestyleHistoryPagination.totalPages === 0
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="ml-4 text-sm text-gray-700">
                  Showing {((page - 1) * size) + 1} to {Math.min(page * size, lifestyleHistoryPagination.totalRecords)} of {lifestyleHistoryPagination.totalRecords} results
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LifestyleHistoryTab;
